import {
  Component,
  Element,
  Event,
  EventEmitter,
  Fragment,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from "@stencil/core";

import {
  StzhToastType,
  StzhUploadCanceledEvent,
  StzhUploadCanceledMultipleEvent,
  StzhUploadCompleteEvent,
  StzhUploadCompleteMultipleEvent,
  StzhUploadErrorEvent,
  StzhUploadErrorMultipleEvent,
  StzhUploadFile,
  StzhUploadFileAddedEvent,
  StzhUploadFileRemovedEvent,
  StzhUploadFileRemoveEvent,
  StzhUploadFilesAddedEvent,
  StzhUploadFilesItem,
  StzhUploadProcessingEvent,
  StzhUploadProcessingMultipleEvent,
  StzhUploadProgressEvent,
  StzhUploadProgressTotalEvent,
  StzhUploadSendingEvent,
  StzhUploadSendingMultipleEvent,
  StzhUploadSuccessEvent,
  StzhUploadSuccessMultipleEvent,
} from "../../index";

import { getStzhEnvironment, hasSlot, isStzhElement, StzhEnvironmentEnum } from "../../utils/utils";
import { StzhUploadLocalizedText } from "./stzh-upload.localization";
import { StzhInputDescription } from "../stzh-input/stzh-input-description";

import "../../libraries/dropzone/dropzone";
import { StzhPhotographingUrlDetailsDto } from "./models/stzh-photographing-url-details.dto";
import { StzhPhotographingConfigurationDto } from "./models/stzh-photographing-configuration.dto";
import QRCode from "qrcode";
import { StzhPhotographingPdfFileDto } from "./models/stzh-photographing-pdf-file.dto";

window.Dropzone.autoDiscover = false;

const ATTR_NAME = "data-dz-name";
const ATTR_TYPE = "data-dz-type";
const ATTR_SIZE = "data-dz-size";
const ATTR_LINK = "data-dz-link";
const ATTR_EXTENSION = "data-dz-extension";
const ATTR_META_ID = "data-dz-meta-id";
const ATTR_ERROR_MESSAGE_ID = "data-dz-error-message-id";
const ATTR_PROGRESS_TEXT = "data-dz-progress-text";
const ATTR_REMOVE = "data-dz-remove";
const ACCEPTED_FILES_DEFAULT = '.jpg,.png,.pdf';

let uploadCounter = 0;
let previewCounter = 0;

// Most properties taken over from:
// https://github.com/dropzone/dropzone/blob/v5.9.3/src/options.js

// Possible events to listen to:
// https://github.com/dropzone/dropzone/blob/v5.9.3/src/dropzone.js#L17

/**
 * @slot error - Slot for error
 */
@Component({
  tag: "stzh-upload",
  styleUrl: "stzh-upload.scss",
})
export class StzhUpload {
  /**
   * Initially pre-existing files (e.g. already uploaded by the user).
   * Array of objects that must at least include `name` and `size` attribute:
   * `[{"name":"file1.txt", "size": 12345}, {"name":"file2.txt", "size": 12345}]`
   */
  @Prop() existingFiles: StzhUploadFilesItem[] | string = [];
  private _existingFiles: StzhUploadFilesItem[] = [];

  /** Name of hidden input field */
  @Prop() name: string = "";

  /** Label */
  @Prop() label: string = "";

  /** Instructions heading */
  @Prop() heading: string = "";

  /** Instructions description */
  @Prop() description: string = "";

  /** Button text */
  @Prop() button: string = "";

  /** Scan Button text */
  @Prop() buttonScan: string = "";

  /** Scan Button text */
  @Prop({ mutable: true }) photographingEnabled: boolean = false;

  /** Disable toasts on upload success and error */
  @Prop() disableToasts: boolean = false;

  /**
   * URL the files are processed to.
   *
   * You can also provide a function that will be called with `files` and
   * `dataBlocks`  and must return the url as string.
   */
  @Prop() url: string | Function = "";

  /**
   * Can be changed to `"put"` if necessary. You can also provide a function
   * that will be called with `files` and must return the method.
   */
  @Prop() method: "post" | "put" | Function = "post";

  /**
   * Will be set on the XHRequest.
   */
  @Prop() withCredentials: boolean = false;

  /**
   * Whether the upload is disabled or not.
   */
  @Prop({ reflect: true }) disabled: boolean = false;

  /**
   * Whether the upload is marked as invalid.
   */
  @Prop({ reflect: true }) invalid: boolean = false;

  /** Whether download functionality should be disabled. */
  @Prop() downloadDisabled: boolean = false;

  /** Whether download functionality should only be shown to completed files. */
  @Prop() downloadDisabledWhileProcessing: boolean = false;

  /** One or multiple error message (use error slot as alternative) */
  @Prop() error: string | string[];
  private _error: string[];

  /**
   * The file types accepted to be uploaded. For security reasons, there's the default value ".jpg,.png,.pdf".
   *
   */
  @Prop({ reflect: true, mutable: true }) acceptedFiles: string = '.jpg,.png,.pdf'; // must match ACCEPTED_FILES_DEFAULT (autodocs can't handle variables)

  /**
   * If false, files will be added to the queue but the queue will not be
   * processed automatically.
   * This can be useful if you need some additional user input before sending
   * files (or if you want all files sent at once).
   * If you're ready to send the file simply call `element.processQueue()`.
   */
  @Prop() autoProcessQueue: boolean = true;

  /**
   * Hide progress (bars and text).
   */
  @Prop() hideProgress: boolean = false;

  /**
   * Hide progress (bars) only while idle (will show them as soon as upload starts).
   * Can be used together with `autoProcessQueue`, e.g. if files are uploaded at form submit to prevent showing empty progressbar.
   */
  @Prop() hideProgressWhileIdle: boolean = false;

  /**
   * If not `null` defines how many files this dropzone handles. If it exceeds,
   * the event `stzhMaxfilesexceeded` will be called.
   */
  @Prop() maxFiles: number | null = null;

  /**
   * The maximum filesize (in MiB) that is allowed to be uploaded per file.
   */
  @Prop() maxFilesize: number = 25;

  /**
   * The maximum filesize (in MiB) that is allowed to be uploaded over all files.
   */
  @Prop() maxFilesizeTotal: number = 100;

  /**
   * How many file uploads to process in parallel.
   */
  @Prop() parallelUploads: number = 2;

  /**
   * Whether to send multiple files in one request. If
   * this it set to true, then the fallback file input element will
   * have the `multiple` attribute as well. This option will
   * also trigger additional events (like `stzhSuccessMultiple`). See the events
   * documentation section for more information.
   */
  @Prop() uploadMultiple: boolean = false;

  /**
   * Whether you want files to be uploaded in chunks to your server. This can't be
   * used in combination with `uploadMultiple`.
   *
   * See `chunksUploaded` prop for the callback to finalise an upload.
   */
  @Prop() chunking: boolean = false;

  /**
   * If `chunking` is enabled, this defines whether **every** file should be chunked,
   * even if the file size is below chunkSize. This means, that the additional chunk
   * form data will be submitted and the `chunksUploaded` callback will be invoked.
   */
  @Prop() forceChunking: boolean = false;

  /**
   * If `chunking` is `true`, then this defines the chunk size in bytes.
   */
  @Prop() chunkSize: number = 2 * 1024 * 1024;

  /**
   * If `true`, the individual chunks of a file are being uploaded simultaneously.
   */
  @Prop() parallelChunkUploads: boolean = false;

  /**
   * Whether a chunk should be retried if it fails.
   */
  @Prop() retryChunks: boolean = false;

  /**
   * If `retryChunks` is true, how many times should it be retried.
   */
  @Prop() retryChunksLimit: number = 3;

  /**
   * The base that is used to calculate the **displayed** filesize. You can
   * change this to 1024 if you would rather display kibibytes, mebibytes,
   * etc... 1024 is technically incorrect, because `1024 bytes` are `1 kibibyte`
   * not `1 kilobyte`. You can change this to `1024` if you don't care about
   * validity.
   */
  @Prop() filesizeBase: number = 1024;

  /**
   * An optional object to send additional headers to the server. Eg:
   * `{ "My-Awesome-Header": "header value" }`
   *
   * Can also be set as function returning the headers object dynamically.
   * If you want to use an async function, use "headersAsync" prop.
   */
  @Prop() headers: object | ((Dropzone: any) => { [key: string]: string }) | null = null;

  /**
   * An optional function providing headers asynchronously. If you use that,
   * it will automatically set 'autoProcessQueue' to 'false'.
   * The component will still automatically process the queue, but in a different
   * way (workaround since dropzone not supporting async headers).
   */
  @Prop() headersAsync: (() => Promise<{ [key: string]: string }>) | null = null;

  /**
   * Should the default headers be set or not?
   * Accept: application/json <- for requesting json response
   * Cache-Control: no-cache <- Request shouldnt be cached
   * X-Requested-With: XMLHttpRequest <- We sent the request via XMLHttpRequest
   */
  @Prop() defaultHeaders: boolean = true;

  /**
   * Whether it is possible to upload files with identical names.
   */
  @Prop() preventDuplicateFileNames: boolean = false;

  /**
   * A function that gets a [file](https://developer.mozilla.org/en-US/docs/DOM/File)
   * and a `done` function as parameters.
   *
   * If the done function is invoked without arguments, the file is "accepted" and will
   * be processed. If you pass an error message, the file is rejected, and the error
   * message will be displayed.
   * This function will not be called if the file is too big or doesn't match the mime types.
   */
  @Prop() accept: Function = function (_file, done) {
    return done();
  };

  /**
   * The callback that will be invoked when all chunks have been uploaded for a file.
   * It gets the file for which the chunks have been uploaded as the first parameter,
   * and the `done` function as second. `done()` needs to be invoked when everything
   * needed to finish the upload process is done.
   */
  @Prop() chunksUploaded: Function = function (_file, done) {
    done();
  };

  /**
   * This is the element the hidden input field (which is used when clicking on the
   * dropzone to trigger file selection) will be appended to. This might
   * be important in case you use frameworks to switch the content of your page.
   *
   * Can be a selector string, or an element directly. Will be rendered into element by default.
   */
  @Prop() hiddenInputContainer: string | HTMLElement;

  /**
   * Prevent clearing hidden input after adding a new file
   * (set true if you use stzh-upload inside a native multipart form with POST method).
   */
  @Prop() preventHiddenInputClear: boolean = false;

  /** Translation strings. */
  @Prop() localization: StzhUploadLocalizedText;

  @State() environment: StzhEnvironmentEnum | undefined;
  @State() totalFileSize: number = 0;
  @State() duplicateDetected: boolean = false;
  @State() totalTooBig: boolean = false;
  @State() totalTooBigMessageHeading: string = "";
  @State() totalTooBigMessageBody: string = "";
  @State() totalTooBigMessageBody2: string = "";
  @State() totalFilesState: Array<any> = [];
  @State() duplicateFileName: string = "";
  @State() duplicateFileSize: string = "";

  @Element() uploadEl: HTMLStzhUploadElement;

  @Listen("reset", { target: "document" })
  resetListener(event: Event) {
    if ((event.target as HTMLElement).contains(this.uploadEl)) {
      requestAnimationFrame(() => {
        this.handleReset();
      });
    }
  }

  @Watch("name")
  nameWatcher(newValue: string) {
    const input = this.hiddenInputContainerElement?.querySelector("input");

    if (input) {
      input.setAttribute("name", newValue);
    }
    this.dropzone.options.paramName = newValue;
  }

  @Watch("maxFiles")
  maxFilesWatcher(newMaxFiles: number | null) {
    this.dropzone.options.maxFiles = newMaxFiles;
  }

  @Watch("maxFilesize")
  maxFilesizeWatcher(newMaxFilesize: number) {
    this.dropzone.options.maxFilesize = newMaxFilesize;
  }

  @Watch("acceptedFiles")
  acceptedFilesWatcher(newAcceptedFiles: string) {
    if (newAcceptedFiles === undefined || newAcceptedFiles === null || newAcceptedFiles === "") {
      this.dropzone.options.acceptedFiles = ACCEPTED_FILES_DEFAULT;
    } else {
      this.dropzone.options.acceptedFiles = newAcceptedFiles;
    }
  }

  @Watch("existingFiles")
  existingFilesWatcher(newValue: StzhUploadFilesItem[] | string) {
    if (typeof newValue === "string") {
      this._existingFiles = JSON.parse(newValue);
    } else {
      this._existingFiles = newValue;
    }
  }

  @Watch("disabled")
  disabledWatcher() {
    if (!this.dropzone) {
      return;
    }

    if (this.disabled) {
      this.dropzone.disable();
    } else {
      this.dropzone.enable();
    }
  }

  @Watch("error")
  errorWatcher(newValue: string | string[]) {
    if (typeof newValue === "string") {
      try {
        this._error = JSON.parse(newValue);
      } catch (e) {
        if (newValue) {
          this._error = [newValue];
        } else {
          this._error = [];
        }
      }
    } else if (newValue) {
      this._error = newValue;
    } else {
      this._error = [];
    }
  }

  /** File added */
  @Event() stzhFileAdded: EventEmitter<StzhUploadFileAddedEvent>;

  /** Files added (multiple) */
  @Event() stzhFilesAdded: EventEmitter<StzhUploadFilesAddedEvent>;

  /** File remove (before, cancelable) */
  @Event() stzhFileRemove: EventEmitter<StzhUploadFileRemoveEvent>;

  /** File removed */
  @Event() stzhFileRemoved: EventEmitter<StzhUploadFileRemovedEvent>;

  /** Before sending file (add additional data before request if necessary) */
  @Event() stzhSending: EventEmitter<StzhUploadSendingEvent>;

  /** Before sending multiple files (add additional data before request if necessary) */
  @Event() stzhSendingMultiple: EventEmitter<StzhUploadSendingMultipleEvent>;

  /** Files starting to get processed */
  @Event() stzhProcessing: EventEmitter<StzhUploadProcessingEvent>;

  /** Files starting to get processed (multiple) */
  @Event() stzhProcessingMultiple: EventEmitter<StzhUploadProcessingMultipleEvent>;

  /** Upload file progress during upload */
  @Event() stzhProgress: EventEmitter<StzhUploadProgressEvent>;

  /** Upload total file progress during upload */
  @Event() stzhProgressTotal: EventEmitter<StzhUploadProgressTotalEvent>;

  /** File error */
  @Event() stzhError: EventEmitter<StzhUploadErrorEvent>;

  /** Files error (multiple files) */
  @Event() stzhErrorMultiple: EventEmitter<StzhUploadErrorMultipleEvent>;

  /** File successfully uploaded */
  @Event() stzhSuccess: EventEmitter<StzhUploadSuccessEvent>;

  /** Files successfully uploaded (multiple files) */
  @Event() stzhSuccessMultiple: EventEmitter<StzhUploadSuccessMultipleEvent>;

  /** Canceled file upload */
  @Event() stzhCanceled: EventEmitter<StzhUploadCanceledEvent>;

  /** Canceled file upload (multiple files) */
  @Event() stzhCanceledMultiple: EventEmitter<StzhUploadCanceledMultipleEvent>;

  /** Completed upload (successfully or with error) */
  @Event() stzhComplete: EventEmitter<StzhUploadCompleteEvent>;

  /** Completed upload (successfully or with error, multiple files) */
  @Event() stzhCompleteMultiple: EventEmitter<StzhUploadCompleteMultipleEvent>;

  /** Sets the focus on the choose file button, when stzh-upload is focused. */
  @Method()
  async setFocus(options?: FocusOptions & { deep?: boolean }) {
    document.querySelector('stzh-flyingfocus')?.activate();
    this.uploadEl.focus(options);
    if (this.chooseFileButtonEl && (options as any)?.deep !== false) {
      this.chooseFileButtonEl.focus(options);
    }
  }

  @Method()
  async processQueue() {
    return this.dropzone.processQueue();
  }

  @Method()
  async removeFile(file, force = false) {
    return this.dropzone.removeFile(file, force);
  }

  @Method()
  async removeAllFiles(cancelIfNecessary = false, force = false) {
    return this.dropzone.removeAllFiles(cancelIfNecessary, force);
  }

  private dropzone: any;
  private dropzoneElement: HTMLElement;
  private chooseFileButtonEl: HTMLStzhButtonElement;
  private clickareaElement: HTMLElement;
  private previewsElement: HTMLElement;
  private errorsElement: HTMLElement;
  private hiddenInputContainerElement: HTMLElement;
  private uploadId: string;

  /** a11y description for the 2 buttons (choose file and photograph) */
  private get a11yDescribedby(): string {
    return [
      `${this.uploadId}-label`,
      `${this.uploadId}-instruction`,
      `${this.uploadId}-description`
    ].join(' ')
  }

  /* Photographing */
  private photographingOverlayElement: HTMLStzhDialogElement;
  private photographingQrcodeElement: HTMLCanvasElement;
  private PHOTOGRAPHING_BASE_URL = "/appl/mobiscan/rs";
  private PHOTOGRAPHING_ENDPOINTS = {
    PING: "/ping",
    URL: "/url",
    IMAGE: "/image",
    CONFIG: "/config",
    AUTH: "/auth",
  };
  @State() isPhotographingAvailable: boolean = false;
  @State() photographingUrlDetails: StzhPhotographingUrlDetailsDto | undefined;
  @State() photographingHost: string;
  @State() photographingConfig: StzhPhotographingConfigurationDto | undefined;
  @State() photographingWebsocket: WebSocket | undefined;
  @State() photographingToken: { access_token: string; expires_in: number } | undefined;
  @State() hiddenInputFiles: File[] = [];

  private handleReset = () => {
    this.removeAllFiles(true);
  };

  private cleanLabel(input) {
    if (input === "" || input === undefined) {
      input = this.localization.photographingLabelDefault;
    }
    let cleanStr: string = input.replaceAll("/", "-");
    // @ts-ignore
    return encodeURIComponent(cleanStr.replaceAll("%", ""));
  }

  private async getToken() {
    if (
      this.photographingToken &&
      this.photographingToken.access_token &&
      Date.now() < this.photographingToken.expires_in
    ) {
      return this.photographingToken;
    } else {
      return await this.requestPhotographingToken();
    }
  }

  private async requestPhotographingToken() {
    try {
      const response = await fetch(
        `${this.photographingHost}${this.PHOTOGRAPHING_BASE_URL}${this.PHOTOGRAPHING_ENDPOINTS.AUTH}`,
      );
      if (!response.ok) {
        throw new Error("Token retrieval failed");
      }
      const parsedResponse = await response.json();
      this.photographingToken = {
        access_token: parsedResponse.access_token,
        expires_in: Date.now() + parsedResponse.expires_in * 1000,
      };
      return this.photographingToken;
    } catch (error) {
      console.error("Token retrieval failed", error);
      throw new Error("Token retrieval failed");
    }
  }

  private async initializePhotographingWithEnvironment(environment: StzhEnvironmentEnum) {
    this.photographingHost = this.getPhotographingHostForEnvironment(environment);
    await this.setPhotographingConfig();
    // if (this.photographingConfig) {
    if (this.photographingConfig && this.photographingEnabled === true) {
      await this.checkPhotographingAvailability();
    }
  }

  private async startPhotographingUserSession() {
    try {
      const response = await fetch(
        `${this.photographingConfig.publicEndpoint}${this.PHOTOGRAPHING_ENDPOINTS.URL}`,
        await this.getPhotographingHttpOptionsWithToken(),
      );
      if (!response.ok) {
        // Nothing to do here. Just don't show the photographing button.
      } else {
        const url: StzhPhotographingUrlDetailsDto = await response.json();
        const urlObject = new URL(url.url);
        const params = new URLSearchParams(urlObject.search);
        this.photographingUrlDetails = {
          url: url.url,
          userId: params.get("id"),
          label: this.cleanLabel(this.label),
        };
        try {
          await QRCode.toCanvas(this.photographingQrcodeElement, url.url + "&label=" + this.cleanLabel(this.label));
        } catch (error) {
          console.error("Error generating QR code:", error);
        }
        await this.establishPhotographingWebsocketConnection();
      }
    } catch (startPhotographingUserSessionException) {
      console.error(
        `StzhUpload.startPhotographingUserSession() - startPhotographingUserSessionException:`,
        startPhotographingUserSessionException,
      );
    }
  }

  private async retrievePhotographingPdf() {
    try {
      const response = await fetch(
        `${this.photographingConfig.publicEndpoint}${this.PHOTOGRAPHING_ENDPOINTS.IMAGE}/retrieve-pdf?id=${this.photographingUrlDetails.userId}&label=${this.cleanLabel(this.label)}`,
        await this.getPhotographingHttpOptionsWithToken(),
      );
      if (!response.ok) {
        console.error(`StzhUpload.retrievePhotographingPdf() - HTTP error! status: ${response.status}`);
      } else {
        const parsedResponse: StzhPhotographingPdfFileDto = await response.json();
        const file = await this.fetchFile(parsedResponse.downloadLink);
        this.dropzone.addFile(file);
        await this.closePhotographingOverlay();
      }
    } catch (retrievePhotographingPdfException) {
      console.error(
        `StzhUpload.retrievePhotographingPdf() - retrievePhotographingPdfException:`,
        retrievePhotographingPdfException,
      );
    }
  }

  private async fetchFile(url) {
    const response = await fetch(url);
    const blob = await response.blob();
    const fileName = url.split("/").pop();
    const cleanFileName: string = decodeURIComponent(fileName.split("?")[0]);
    if (blob.type === "application/octet-stream") {
      return new File([blob], cleanFileName, { type: "application/pdf" });
    }
    return new File([blob], cleanFileName, { type: blob.type });
  }

  private async establishPhotographingWebsocketConnection() {
    this.photographingWebsocket = new WebSocket(
      `${this.photographingConfig.wsClientUrl}${this.photographingUrlDetails.userId}`,
      ["chat", (await this.getToken()).access_token],
    );

    this.photographingWebsocket.onopen = () => {
      setInterval(() => {
        this.sendPhotographingWebsocketMessage("Ping");
      }, 20000);
    };

    this.photographingWebsocket.onmessage = async event => {
      const message = event.data;

      if (message === "retrievePdf") {
        await this.retrievePhotographingPdf();
      }
    };

    this.photographingWebsocket.onerror = error => {
      console.error("WebSocket error: ", error);
    };
  }

  private closePhotographingWebsocketConnection = () => {
    this.photographingWebsocket?.close();
  };

  private sendPhotographingWebsocketMessage(message: any) {
    this.photographingWebsocket.send(message);
  }

  private async getPhotographingHttpOptionsWithToken() {
    const token = await this.getToken();
    return {
      headers: {
        Authorization: `Bearer ${token.access_token}`,
      },
    };
  }

  private async checkPhotographingAvailability() {
    try {
      const response = await fetch(
        `${this.photographingConfig.publicEndpoint}${this.PHOTOGRAPHING_ENDPOINTS.PING}`,
        await this.getPhotographingHttpOptionsWithToken(),
      );
      if (!response.ok) {
        console.error(`StzhUpload.isAvailable() - HTTP error! status: ${response.status}`);
        this.isPhotographingAvailable = false;
      } else {
        this.isPhotographingAvailable = true;
      }
    } catch (isAvailableException) {
      console.error(`StzhUpload.isAvailable() - isAvailableException:`, isAvailableException);
      this.isPhotographingAvailable = false;
    }
  }

  private async setPhotographingConfig() {
    try {
      const response = await fetch(
        `${this.photographingHost}${this.PHOTOGRAPHING_BASE_URL}${this.PHOTOGRAPHING_ENDPOINTS.CONFIG}`,
      );
      if (!response.ok) {
        console.error(`StzhUpload.setPhotographingConfig() - HTTP error! status: ${response.status}`);
      } else {
        this.photographingConfig = await response.json();
      }
    } catch (getConfigException) {
      console.error(`StzhUpload.setPhotographingConfig() - getConfigException:`, getConfigException);
    }
  }

  private getPhotographingHostForEnvironment(environment: "DEV" | "UAT" | "PROD" | "UNKNOWN") {
    switch (environment) {
      case "DEV":
        return "https://www.test.stadt-zuerich.loc";
      case "UAT":
        return "https://www.integ.stadt-zuerich.ch";
      case "PROD":
        return "https://www.stadt-zuerich.ch";
      case "UNKNOWN":
        this.PHOTOGRAPHING_BASE_URL = `/stzh-components/assets/mock${this.PHOTOGRAPHING_BASE_URL}`;
        return `${window.location.origin}`;
    }
  }

  private onButtonScanClick = event => {
    event.preventDefault();
    event.stopPropagation();
    this.photographingOverlayElement.show().then();
    if (this.isPhotographingAvailable) {
      this.startPhotographingUserSession()
        .then()
        .catch(photographingUserSessionException => {
          console.error("photographingUserSessionException", photographingUserSessionException);
        });
    }
  };

  private closePhotographingOverlay = async () => {
    this.photographingOverlayElement.hide().catch();
  };

  private filesize = function (size) {
    let selectedSize = 0;
    let selectedUnit = "b";

    if (size > 0) {
      let units = ["tb", "gb", "mb", "kb", "b"];

      for (let i = 0; i < units.length; i++) {
        let unit = units[i];
        let cutoff = Math.pow(this.options.filesizeBase, 4 - i) / 10;

        if (size >= cutoff) {
          selectedSize = size / Math.pow(this.options.filesizeBase, 4 - i);
          selectedUnit = unit;
          break;
        }
      }

      selectedSize = Math.round(selectedSize * 100) / 100; // Cutting of digits
    }

    return `<strong>${selectedSize}</strong> ${this.options.dictFileSizeUnits[selectedUnit]}`;
  };

  private filePreviewId = function (file): string {
    return `${this.uploadId}-${file._id}`;
  };

  private removeFileFromState(file: StzhUploadFile) {
    this.totalFilesState = this.totalFilesState.filter(f => f._id !== file._id);

    // remove from hiddenInputFiles, falls relevant
    if (this.preventHiddenInputClear) {
      this.hiddenInputFiles = this.hiddenInputFiles.filter(trackedFile => trackedFile.name !== file.name);
      // Aktualisiere das hidden file input Feld:
      const hiddenFileInput = this.hiddenInputContainerElement?.querySelector("input");
      if (hiddenFileInput) {
        const dataTransfer = new DataTransfer();
        this.hiddenInputFiles.forEach(trackedFile => {
          dataTransfer.items.add(trackedFile);
        });
        hiddenFileInput.files = dataTransfer.files;
      }
    }
  }

  async componentWillLoad() {
    this.uploadId = `stzh-upload-${uploadCounter++}`;

    this.existingFilesWatcher(this.existingFiles);
    this.errorWatcher(this.error);

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.uploadEl, "upload");
    }

    this.environment = getStzhEnvironment();
    if (this.photographingEnabled) {
      this.initializePhotographingWithEnvironment(this.environment).catch(
        initializePhotographingWithEnvironmentException => {
          console.error(
            "initializePhotographingWithEnvironmentException",
            initializePhotographingWithEnvironmentException,
          );
        },
      );
    }
  }

  private toggleToast(message: string, type: StzhToastType) {
    if (!this.disableToasts) {
      document.querySelector("stzh-upload").querySelector("stzh-toastbar").toast(message, {
        type: type,
      });
    }
  }

  componentDidRender() {
    if (this.dropzone) {
      return;
    }

    const self = this;

    function enableDownload(file) {
      let fileHref = file.url || "";

      if (file.upload) {
        fileHref = URL.createObjectURL(file);
      }

      if (fileHref) {
        for (var node of file.previewElement.querySelectorAll(`[${ATTR_LINK}]`)) {
          node.setAttribute("href", fileHref);
          node.setAttribute("download", file.name);

          const isStzh = isStzhElement(node);
          const fileId = self.filePreviewId(file);

          node.setAttribute(isStzh ? "analytics-id" : "s-object-id", `${self.localization.downloadFile} ${file.name}`);
          node.setAttribute(isStzh ? "a11y-label" : "aria-label", `${self.localization.downloadFile} ${file.name}`);
          node.setAttribute(isStzh ? "a11y-describedby" : "aria-describedby", `${fileId}-error-message ${fileId}-meta`);
        }
      }
    }

    async function prepareRemovalTransition(id: string): Promise<void> {
      const wrapper: HTMLElement = document.getElementById(id);
      if (wrapper) {
        wrapper.classList.add("stzh-upload__fade-out");
      }
    }

    async function removeFileByClick(id: string): Promise<void> {
      const wrapper: HTMLElement = document.getElementById(id);
      if (wrapper) {
        const button: HTMLElement = wrapper.querySelector(".stzh-button");
        if (button) {
          button.click();
        }
      }
    }

    if (this.headersAsync !== null) {
      this.autoProcessQueue = false;
    }

    this.dropzone = new window.Dropzone(this.dropzoneElement, {
      accept: this.accept,
      acceptedFiles: this.acceptedFiles,
      autoProcessQueue: this.autoProcessQueue,
      chunking: this.chunking,
      chunkSize: this.chunkSize,
      chunksUploaded: this.chunksUploaded,
      clickable: [this.clickareaElement],
      createImageThumbnails: false,
      defaultHeaders: this.defaultHeaders,
      defaultLabel: this.localization.photographingLabelDefault,
      dictCancelUpload: this.localization.cancelUpload,
      dictCancelUploadConfirmation: this.localization.cancelUploadConfirmation,
      dictDeleteFile: this.localization.deleteFile,
      dictFileSizeUnits: { tb: "TiB", gb: "GiB", mb: "MiB", kb: "KiB", b: "b" },
      dictFileTooBig: this.localization.fileTooBig,
      dictInvalidFileType: this.localization.invalidFileType,
      dictMaxFilesExceeded: this.localization.maxFilesExceeded,
      dictRemoveFile: this.localization.removeFile,
      dictRemoveFileConfirmation: null,
      dictResponseError: this.localization.responseError,
      dictUploadCanceled: this.localization.uploadCanceled,
      duplicateFileName: this.duplicateFileName,
      duplicateFileSize: this.duplicateFileSize,
      duplicateFileUploadHeading: this.localization.duplicateFileUploadHeading,
      duplicateFileUploadParagraph: this.localization.duplicateFileUploadParagraph,
      filesize: this.filesize,
      filesizeBase: this.filesizeBase,
      forceChunking: this.forceChunking,
      headers: this.headers,
      hiddenInputContainer: this.hiddenInputContainer ? this.hiddenInputContainer : this.hiddenInputContainerElement,
      maxFiles: this.maxFiles,
      maxFilesize: this.maxFilesize,
      method: this.method,
      parallelChunkUploads: this.parallelChunkUploads,
      parallelUploads: this.parallelUploads,
      preventDuplicateFileNames: this.preventDuplicateFileNames,
      preventHiddenInputClear: this.preventHiddenInputClear,
      previewsContainer: this.previewsElement,
      retryChunks: this.retryChunks,
      retryChunksLimit: this.retryChunksLimit,
      totalFilesState: this.totalFilesState,
      uploadMultiple: this.uploadMultiple,
      url: this.url,
      withCredentials: this.withCredentials,
      // Called when a file is dropped to the dropzone
      // Receives `event`
      drop: e => {
        // Preserve files in hidden input when preventHiddenInputClear is true
        if (this.preventHiddenInputClear) {
          const hiddenFileInput = this.hiddenInputContainerElement?.querySelector("input");
          const dataTransfer = new DataTransfer();
          for (let i = 0; i < hiddenFileInput.files.length; i++) {
            dataTransfer.items.add(hiddenFileInput.files[i]);
          }
          for (const file of e.dataTransfer.files) {
            dataTransfer.items.add(file);
          }
          hiddenFileInput.files = dataTransfer.files;
        }
        return this.uploadEl.classList.remove("dz-drag-hover");
      },

      // Called when a file is added to the queue
      // Receives `file`
      // Taken from https://github.com/dropzone/dropzone/blob/v5.9.3/src/options.js#L594
      async addedfile(file) {
        file._id = previewCounter++;

        self.totalFilesState.push(...self.totalFilesState, file);

        // Preserve files in hidden input when preventHiddenInputClear is true
        if (self.preventHiddenInputClear) {
          const hiddenFileInput = self.hiddenInputContainerElement?.querySelector("input");

          if (hiddenFileInput) {
            let fileAlreadyExists = false;

            if (file instanceof File) {
              fileAlreadyExists = self.hiddenInputFiles.some(
                existingFile => existingFile.name === file.name && existingFile.size === file.size,
              );

              if (!fileAlreadyExists) {
                self.hiddenInputFiles.push(file);
              }
            }

            if (self.hiddenInputFiles.length > 0) {
              const dataTransfer = new DataTransfer();

              self.hiddenInputFiles.forEach(trackedFile => {
                dataTransfer.items.add(trackedFile);
              });

              hiddenFileInput.files = dataTransfer.files;
            }
          }
        }

        if (self.preventDuplicateFileNames) {
          if (self.totalFilesState.length > 0) {
            self.duplicateDetected = false; // reset
            self.totalFilesState.forEach(f => {
              if (file.name === f.name && file._id !== f._id) {
                self.duplicateDetected = true;
                self.duplicateFileName = file.name;
                self.duplicateFileSize = (file.size / (1024 * 1024)).toFixed(2) + " MiB";
                setTimeout(prepareRemovalTransition, 10, "_" + file._id);
                setTimeout(removeFileByClick, 2000, "_" + file._id);
              }
            });
          }
        }

        if (this.element === this.previewsContainer) {
          this.element.classList.add("dz-started");
        }

        const fileSizeInMiB = file.size / (1024 * 1024);

        if (self.totalFileSize + fileSizeInMiB > self.maxFilesizeTotal) {
          self.totalTooBig = true;
          self.totalTooBigMessageHeading = self.localization.uploadFailed;
          self.totalTooBigMessageBody = self.localization.fileTooBigTotal.replace(
            "{{totalFilesize}}",
            `${(self.totalFileSize + fileSizeInMiB).toFixed(1)}`,
          );
          self.totalTooBigMessageBody2 = self.localization.fileTooBigTotal2.replace(
            "{{maxFilesizeTotal}}",
            `${self.maxFilesizeTotal}`,
          );

          self.totalFilesState = self.totalFilesState.filter(f => f.name !== file.name);
          return;
        } else {
          self.totalTooBig = false;
        }

        self.totalFileSize += fileSizeInMiB;

        if (this.previewsContainer && !this.options.disablePreviews) {
          file.previewElement = window.Dropzone.createElement(this.options.previewTemplate.trim());
          file.previewElement.setAttribute("id", "_" + file._id);
          file.previewTemplate = file.previewElement; // Backwards compatibility
          this.previewsContainer.appendChild(file.previewElement);

          if (!file.size) {
            file.previewElement.classList.add("dz-empty");
          }

          for (var node of file.previewElement.querySelectorAll(`[${ATTR_NAME}]`)) {
            node.textContent = file.name;
          }

          for (node of file.previewElement.querySelectorAll(`[${ATTR_SIZE}]`)) {
            node.innerHTML = self.filesize.call(this, file.size);
          }

          for (node of file.previewElement.querySelectorAll(`[${ATTR_META_ID}]`)) {
            node.setAttribute("id", `${self.filePreviewId(file)}-meta`);
          }

          for (node of file.previewElement.querySelectorAll(`[${ATTR_ERROR_MESSAGE_ID}]`)) {
            node.setAttribute("id", `${self.filePreviewId(file)}-error-message`);
          }

          if (this.options.addRemoveLinks) {
            file._removeLink = window.Dropzone.createElement(
              `<a class="dz-remove" href="javascript:undefined;" ${ATTR_REMOVE}>
                ${this.options.dictRemoveFile}
              </a>`,
            );
            file.previewElement.appendChild(file._removeLink);
          }

          let removeFileEvent = e => {
            e.preventDefault();
            e.stopPropagation();
            if (file.status === window.Dropzone.UPLOADING) {
              return window.Dropzone.confirm(this.options.dictCancelUploadConfirmation, () => this.removeFile(file));
            } else {
              if (this.options.dictRemoveFileConfirmation) {
                return window.Dropzone.confirm(this.options.dictRemoveFileConfirmation, () => this.removeFile(file));
              } else {
                return this.removeFile(file);
              }
            }
          };

          for (let removeLink of file.previewElement.querySelectorAll(`[${ATTR_REMOVE}]`)) {
            removeLink.addEventListener("click", removeFileEvent);

            const isStzh = isStzhElement(removeLink);
            const fileId = self.filePreviewId(file);

            removeLink.setAttribute(isStzh ? "analytics-id" : "s-object-id", `${this.options.dictRemoveFile}`);
            removeLink.setAttribute(
              isStzh ? "a11y-label" : "aria-label",
              `${this.options.dictRemoveFile} ${file.name}`,
            );
            removeLink.setAttribute(
              isStzh ? "a11y-describedby" : "aria-describedby",
              `${fileId}-error-message ${fileId}-meta`,
            );
          }

          if (!self.downloadDisabled && !self.downloadDisabledWhileProcessing) {
            enableDownload(file);
          }
        }

        // Support async headers.
        if (self.headersAsync !== null) {
          self.dropzone.options.headers = await self.headersAsync();
          self.dropzone.processQueue();
        }
      },

      // When the upload is finished, either with success or an error.
      // Taken from https://github.com/dropzone/dropzone/blob/v5.9.3/src/options.js#L752
      complete(file) {
        for (let node of file.previewElement.querySelectorAll(`[${ATTR_REMOVE}]`)) {
          const hasErrorState = file.status === "error";
          const removeOrDeleteLabel = hasErrorState ? this.options.dictRemoveFile : this.options.dictDeleteFile;
          const isStzh = isStzhElement(node);
          const fileId = self.filePreviewId(file);

          node.setAttribute(isStzh ? "a11y-label" : "aria-label", `${removeOrDeleteLabel} ${file.name}`);
          node.setAttribute(isStzh ? "a11y-describedby" : "aria-describedby", `${fileId}-error-message ${fileId}-meta`);
        }

        // move file uploads with errors to another element, so they are grouped together
        if (file.status === "error") {
          self.errorsElement.appendChild(file.previewElement);
        } else if (!self.downloadDisabled && self.downloadDisabledWhileProcessing) {
          enableDownload(file);
        }

        if (file._removeLink) {
          file._removeLink.innerHTML = this.options.dictRemoveFile;
        }

        if (file.previewElement) {
          return file.previewElement.classList.add("dz-complete");
        }
      },

      // Called whenever a file is removed.
      // Taken fom https://github.com/dropzone/dropzone/blob/v5.9.3/src/options.js#L649
      removedfile(file) {
        if (file.previewElement != null && file.previewElement.parentNode != null) {
          file.previewElement.parentNode.removeChild(file.previewElement);
        }

        const fileSizeInMiB = file.size / (1024 * 1024);
        self.totalFileSize -= fileSizeInMiB;

        // set focus to upload
        self.chooseFileButtonEl.focus();
        return this._updateMaxFilesReachedClass();
      },
      error(file, message, xhr) {
        if (file.previewElement) {
          file.previewElement.classList.add("dz-error");

          if (typeof message !== "string" && message.error) {
            message = message.error;
          } else if (xhr) {
            if (xhr.status === 401 || xhr.status === 403) {
              message = self.localization.accessError;
            } else if (xhr.status === 413) {
              message = self.localization.fileTooBigServer;
            } else if (xhr.status === 415) {
              message = self.localization.invalidFileType;
            } else if (xhr.status >= 500) {
              message = self.localization.serverError;
            } else {
              const defaultError = self.localization.responseError.replace("{{statusCode}}", xhr.status);

              message = defaultError;
            }
          }

          for (let node of file.previewElement.querySelectorAll("[data-dz-errormessage]")) {
            node.textContent = message;
          }
        }
      },
      previewTemplate: `
      <div class="stzh-upload__preview">
        <div class="stzh-upload__image">
          <img data-dz-thumbnail />
        </div>

        <div class="stzh-upload__details">
          <div class="stzh-upload__info-wrapper">
            <div class="stzh-upload__info-wrapper-processing">
              <div class="stzh-upload__infos">
                <div class="stzh-upload__subtitle">
                  <a class="stzh-upload__link" ${ATTR_NAME} ${ATTR_LINK}></a>
                </div>
                <div class="stzh-upload__progress-text">
                  <span ${ATTR_PROGRESS_TEXT}></span>
                </div>
              </div>
              <div class="stzh-upload__meta">
                <span class="stzh-upload__extension" ${ATTR_EXTENSION}></span>
                <span class="stzh-upload__separator"></span>
                <span class="stzh-upload__size" ${ATTR_SIZE}></span>
              </div>
              <div class="stzh-upload__progress">
                <span class="stzh-upload__progress-bar" data-dz-uploadprogress></span>
              </div>
            </div>
            <div class="stzh-upload__info-wrapper-complete">
              <div class="stzh-upload__title">
                <a class="stzh-upload__link" ${ATTR_NAME} ${ATTR_LINK}></a>
              </div>
              <div class="stzh-upload__meta" ${ATTR_META_ID}>
                <span class="stzh-upload__extension" ${ATTR_EXTENSION}></span>
                <span class="stzh-upload__separator"></span>
                <span class="stzh-upload__size" ${ATTR_SIZE}></span>
              </div>
              <div class="stzh-upload__error-message" ${ATTR_ERROR_MESSAGE_ID}>
                <span data-dz-errormessage></span>
              </div>
            </div>
          </div>
          <div class="stzh-upload__actions">
            <!-- <stzh-button variant="tertiary" size="small" icon-only icon="download" class="stzh-upload__download" ${ATTR_LINK}></stzh-button> -->
            <stzh-button variant="tertiary" size="small" icon-only class="stzh-upload__delete" ${ATTR_REMOVE}>
              <stzh-icon slot="icon" class="stzh-upload__delete-icon" name="delete"></stzh-icon>
              <stzh-icon slot="icon" class="stzh-upload__delete-icon-error" name="close"></stzh-icon>
            </stzh-button>
          </div>
        </div>
        </div>
      </div>
      `,
    });

    this.nameWatcher(this.name);
    this.disabledWatcher();

    this.dropzone.on("addedfile", (file: StzhUploadFile) =>
      this.stzhFileAdded.emit({
        component: "stzh-upload",
        file,
      }),
    );

    this.dropzone.on("addedfiles", (files: StzhUploadFile[]) =>
      this.stzhFilesAdded.emit({
        component: "stzh-upload",
        files,
      }),
    );

    this.dropzone.on("removefile", (file: StzhUploadFile) => {
      this.removeFileFromState(file);

      return (
        this.stzhFileRemove.emit({
          component: "stzh-upload",
          file,
        }).defaultPrevented === false
      );
    });

    this.dropzone.on("removedfile", (file: StzhUploadFile, force: boolean) =>
      this.stzhFileRemoved.emit({
        component: "stzh-upload",
        file,
        force,
      }),
    );

    this.dropzone.on("sending", (file: StzhUploadFile, xhr: XMLHttpRequest, formData: FormData) =>
      this.stzhSending.emit({
        component: "stzh-upload",
        file,
        xhr,
        formData,
      }),
    );

    this.dropzone.on("sendingmultiple", (files: StzhUploadFile[], xhr: XMLHttpRequest, formData: FormData) =>
      this.stzhSendingMultiple.emit({
        component: "stzh-upload",
        files,
        xhr,
        formData,
      }),
    );

    this.dropzone.on("processing", (file: StzhUploadFile) =>
      this.stzhProcessing.emit({
        component: "stzh-upload",
        file,
      }),
    );

    this.dropzone.on("processingmultiple", (files: StzhUploadFile[]) =>
      this.stzhProcessingMultiple.emit({
        component: "stzh-upload",
        files,
      }),
    );

    this.dropzone.on("uploadprogress", (file: StzhUploadFile, progress: number, bytesSent: number) =>
      this.stzhProgress.emit({
        component: "stzh-upload",
        file,
        progress,
        bytesSent,
      }),
    );

    this.dropzone.on("totaluploadprogress", (totalUploadProgress: number, totalBytes: number, totalBytesSent: number) =>
      this.stzhProgressTotal.emit({
        component: "stzh-upload",
        totalUploadProgress,
        totalBytes,
        totalBytesSent,
      }),
    );

    this.dropzone.on("error", (file: StzhUploadFile, message: string, xhr: XMLHttpRequest) => {
      if (xhr && xhr.status >= 400) {
        this.toggleToast(this.localization.uploadErrorToast, "error");
      }
      this.stzhError.emit({
        component: "stzh-upload",
        file,
        message,
        xhr,
      });
    });

    this.dropzone.on("errormultiple", (files: StzhUploadFile[], message: string, xhr: XMLHttpRequest) => {
      if (xhr && xhr.status >= 400) {
        this.toggleToast(this.localization.uploadErrorToast, "error");
      }
      this.stzhErrorMultiple.emit({
        component: "stzh-upload",
        files,
        message,
        xhr,
      });
    });

    this.dropzone.on("success", (file: StzhUploadFile, responseText: string, event: ProgressEvent) => {
      this.toggleToast(this.localization.uploadSuccessToast, "success");
      this.stzhSuccess.emit({
        component: "stzh-upload",
        file,
        responseText,
        event,
      });
    });

    this.dropzone.on("successmultiple", (files: StzhUploadFile[], responseText: string, event: ProgressEvent) => {
      this.toggleToast(this.localization.uploadSuccessToast, "success");
      this.stzhSuccessMultiple.emit({
        component: "stzh-upload",
        files,
        responseText,
        event,
      });
    });

    this.dropzone.on("canceled", (file: StzhUploadFile) =>
      this.stzhCanceled.emit({
        component: "stzh-upload",
        file,
      }),
    );

    this.dropzone.on("canceledmultiple", (files: StzhUploadFile[]) =>
      this.stzhCanceledMultiple.emit({
        component: "stzh-upload",
        files,
      }),
    );

    this.dropzone.on("complete", (file: StzhUploadFile) =>
      this.stzhComplete.emit({
        component: "stzh-upload",
        file,
      }),
    );

    this.dropzone.on("completemultiple", (files: StzhUploadFile[]) =>
      this.stzhCompleteMultiple.emit({
        component: "stzh-upload",
        files,
      }),
    );

    this.dropzone.on("addedfile", (file: StzhUploadFile) => {
      file.previewElement.querySelectorAll(`[${ATTR_NAME}]`).forEach((name: HTMLElement) => {
        name.setAttribute("title", file.name);
      });

      file.previewElement.querySelectorAll(`[${ATTR_TYPE}]`).forEach((type: HTMLElement) => {
        type.innerText = file.type;
      });

      file.previewElement.querySelectorAll(`[${ATTR_EXTENSION}]`).forEach((extension: HTMLElement) => {
        extension.innerText = file.name.split(".").pop();
      });
    });

    this.dropzone.on("uploadprogress", (file: StzhUploadFile, progress: number) => {
      file.previewElement.querySelectorAll(`[${ATTR_PROGRESS_TEXT}]`).forEach((progressText: HTMLElement) => {
        progressText.innerText = `${progress.toString()}%`;
      });
    });

    this._existingFiles.forEach(file => {
      this.dropzone.displayExistingFile(file, null, null, null, null);
    });
  }

  disconnectedCallback() {
    if (this.dropzone) {
      this.dropzone.destroy();
      this.dropzone = null;
    }
  }

  render() {
    const errorUsed = hasSlot(this.uploadEl, "error") || this._error?.length > 0;
    const classes = {
      "stzh-upload": true,
      "stzh-upload--is-invalid": this.invalid || errorUsed,
      "stzh-upload--is-disabled": this.disabled,
      "stzh-upload--has-hidden-progress": this.hideProgress,
      "stzh-upload--has-hidden-progress-while-idle": this.hideProgressWhileIdle,
      "stzh-upload--has-error": errorUsed,
      "stzh-upload--has-instructions": !!this.description || !!this.localization.description,
    };
    const fileRestrictionsLocalization = this.localization.description;
    const totalFileSizeLimitLocalization = this.localization.descriptionTotalFileSizeLimit;

    return (
      <Host
        tabindex={this.disabled ? null : "-1"}
        is-invalid={this.invalid || errorUsed}
        aria-invalid={(this.invalid || errorUsed) ? 'true' : 'false'}
        aria-describedby={((this.invalid || errorUsed)) ? this.error : undefined}
        role="group">
        <stzh-toastbar />
        <div class={classes} ref={el => (this.dropzoneElement = el as HTMLDivElement)}>
          <div ref={el => (this.hiddenInputContainerElement = el as HTMLElement)}></div>

          <div id={`${this.uploadId}-label`}>{this.label && <div class="stzh-upload__label">{this.label}</div>}</div>

          <div class="stzh-upload__clickarea" ref={el => (this.clickareaElement = el as HTMLDivElement)}>
            <div class="stzh-upload__heading-wrapper">
              <div class="stzh-upload__heading-inner-wrapper">
                <div class="stzh-upload__heading">{this.heading ? this.heading : this.localization.heading}</div>
                <div class="stzh-upload__buttons-wrapper">
                  <stzh-button
                    id={`${this.uploadId}-button`}
                    class="stzh-upload__button"
                    variant="secondary"
                    size="small"
                    ref={el => (this.chooseFileButtonEl = el as HTMLStzhButtonElement)}
                    label={this.button ? this.button : this.localization.button}
                    a11yDescribedby={this.a11yDescribedby}
                    a11yControls={`${this.uploadId}-previews`}
                    aria-invalid={(this.invalid || errorUsed) ? 'true' : 'false'}
                    disabled={this.disabled}
                  >
                    <stzh-icon slot="icon" name="upload"></stzh-icon>
                  </stzh-button>
                  {this.isPhotographingAvailable && (
                    <stzh-button
                      onClick={this.onButtonScanClick}
                      class="stzh-upload__button-scan"
                      variant="secondary"
                      size="small"
                      label={this.buttonScan ? this.buttonScan : this.localization.buttonScan}
                      a11yDescribedby={this.a11yDescribedby}
                      aria-invalid={(this.invalid || errorUsed) ? 'true' : 'false'}
                      disabled={this.disabled}
                    >
                      <stzh-icon slot="icon" name="camera"></stzh-icon>
                    </stzh-button>
                  )}
                </div>
                <div class="stzh-upload__hint-wrapper">
                  <span>{this.localization.buttonIntro}</span>
                </div>
              </div>
            </div>
            <div class="stzh-upload__instructions" id={`${this.uploadId}-instruction`}>
              <p>
                {this.description && (
                  <span class="stzh-upload__instructions__description">
                    {this.description}
                    <br />
                  </span>
                )}
                {fileRestrictionsLocalization
                  .replace("{{fileformats}}", this.acceptedFiles)
                  .replace("{{filesize}}", `${this.maxFilesize}`)}
                <br />
                {totalFileSizeLimitLocalization.replace("{{maxFilesizeTotal}}", `${this.maxFilesizeTotal}`)}
              </p>
            </div>
          </div>

          {errorUsed && (
            <div class="stzh-upload__description-wrapper">
              <StzhInputDescription classPrefix="stzh-upload" id={`${this.uploadId}-description`} error={this._error} />
            </div>
          )}
          <div id={`${this.uploadId}-previews`} class="stzh-upload__previews-wrapper">
            <div class="stzh-upload__previews-inner-wrapper">
              <div class="stzh-upload__errors" ref={el => (this.errorsElement = el as HTMLElement)}></div>
              <div
                class="stzh-upload__previews dropzone-previews"
                ref={el => (this.previewsElement = el as HTMLElement)}
              ></div>
            </div>
          </div>
        </div>

        {this.duplicateDetected && (
          <div class="stzh-upload__previews-message-wrapper">
            <stzh-message
              type="error"
              label={this.localization.duplicateFileUploadHeading}
              onStzhClose={() => (this.duplicateDetected = false)}
            >
              <stzh-text>
                <p>
                  «{this.duplicateFileName}» ({this.duplicateFileSize})
                </p>
                <p>{this.localization.duplicateFileUploadParagraph}</p>
              </stzh-text>
            </stzh-message>
          </div>
        )}

        {this.totalTooBig && (
          <div class="stzh-upload__previews-message-wrapper">
            <stzh-message
              type="warning"
              label={this.totalTooBigMessageHeading}
              onStzhClose={() => (this.totalTooBig = false)}
            >
              <stzh-text>
                <p>
                  {this.totalTooBigMessageBody} {this.totalTooBigMessageBody2}
                </p>
              </stzh-text>
            </stzh-message>
          </div>
        )}

        <stzh-dialog
          ref={el => (this.photographingOverlayElement = el as HTMLStzhDialogElement)}
          onStzhClose={this.closePhotographingWebsocketConnection}
          class={this.isPhotographingAvailable && "stzh-dialog--fit-content"}
          heading={this.localization.photographingTitle1}
        >
          <stzh-button
            class="stzh-upload-photographing__back-button"
            icon="arrow-left"
            size="small"
            variant="tertiary-plain"
            no-padding-left="true"
            no-padding-right="true"
            onClick={this.closePhotographingOverlay}
          >
            {this.localization.back}
          </stzh-button>
          {this.isPhotographingAvailable && (
            <Fragment>
              <div class="stzh-upload-photographing__info">
                <div class="stzh-upload-photographing__text-qrcode">
                  <div class="stzh-upload-photographing-qrlink-container">
                    <stzh-text>{this.localization.photographingText1}</stzh-text>
                  </div>
                  <div class="stzh-upload-photographing-qrcode-container">
                    <canvas
                      class="stzh-upload-photographing-qrcode"
                      ref={el => (this.photographingQrcodeElement = el as HTMLCanvasElement)}
                    ></canvas>
                  </div>
                </div>
              </div>
              <div class="stzh-upload-photographing__info">
                <stzh-heading level="3">{this.localization.photographingTitle2}</stzh-heading>
                <stzh-text>{this.localization.photographingText2}</stzh-text>
              </div>
              <div class="stzh-upload-photographing__continue">
                {!!this.photographingUrlDetails && (
                  <stzh-link
                    class="stzh-upload-photographing-link"
                    href={this.photographingUrlDetails.url + "&label=" + this.cleanLabel(this.label)}
                    target="_blank"
                  >
                    {this.localization.photographingContinue}
                  </stzh-link>
                )}
              </div>
            </Fragment>
          )}
        </stzh-dialog>
      </Host>
    );
  }
}
